import React, { forwardRef, useEffect, useState } from 'react';
import type { AtomResult } from './useAtomizer';
type SendState = 'idle' | 'sending' | 'sent' | 'error';
type Fields = {
  name: string;
  email: string;
  company: string;
};
type AtomLedgerProps = {
  result: AtomResult;
  inline?: boolean;
  libOpen: boolean;
  setLibOpen: (open: boolean) => void;
  send: SendState;
  submitLibrary: (fields: Fields) => void;
};
const FORCE_CLASS = {
  MUST: 'must',
  NEVER: 'never',
  SHOULD: 'should'
} as const;

/** Counts from 0 to `to` over ~600ms; jumps straight there under reduced motion. */
function useCountUp(to: number) {
  const [n, setN] = useState(to);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setN(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / 600);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setN(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return n;
}
function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}
export const AtomLedger = forwardRef<HTMLDivElement, AtomLedgerProps>(function AtomLedger({
  result,
  inline,
  libOpen,
  setLibOpen,
  send,
  submitLibrary
}, ref) {
  const [fields, setFields] = useState<Fields>({
    name: '',
    email: '',
    company: ''
  });
  const total = result.total;
  const shownTotal = useCountUp(total);
  const shown = result.rows.length;
  return <div ref={ref} id={`ledger-${result.id}`} className={`ledger${inline ? ' ledger-inline' : ''}`} aria-live="polite" data-id="element-102" data-mp="1ncjn5od5gjly">
      <div className="ledger-hd" data-id="element-103" data-mp="1sbwkx45k7b3b">
        <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 14,
        flexWrap: 'wrap'
      }} data-id="element-104" data-mp="1sd7lyw7me4qq">
          <div className="big" aria-label={`${total} rules found`} data-id="element-105" data-mp="3hogk1wud2hoe">
            {shownTotal}
          </div>
          <div className="lab" data-id="element-106" data-mp="3kaqzp8n48bfs">RULES FOUND</div>
          {result.binding !== undefined && <div className="lab" style={{
          color: 'var(--amber)'
        }} data-id="element-107" data-mp="3k8ecoh2i3avv">
              {result.binding} binding · {plural(result.conflicts ?? 0, 'conflict', 'conflicts')} found
            </div>}
        </div>
        <div className="src" data-id="element-108" data-mp="36b7uooplpg7p">{result.label}</div>
      </div>
      <div role="table" aria-label="Rules found" data-id="element-109" data-mp="36csm45gmcj1c">
        <div className="lrow h" role="row" data-id="element-110" data-mp="7tu3c7aql3xi">
          <span role="columnheader" data-id="element-111" data-mp="7sj0qat1227z">FORCE</span>
          <span role="columnheader" data-id="element-112" data-mp="2tpmuh9tw97a">RULE</span>
          <span role="columnheader" data-id="element-113" data-mp="2v11xd8q53dp">TRIGGER</span>
          <span role="columnheader" data-id="element-114" data-mp="3u8di453mil4i">SOURCE</span>
          <span role="columnheader" data-id="element-115" data-mp="3u72fi8lwzjez">THEIR WORDS</span>
        </div>
        {result.rows.map((r, i) => <div className="lrow in" role="row" key={i} style={{
        animationDelay: `${i * 40}ms`
      }} data-id="element-116" data-mp="3p9k6ki7wd46z" data-mpi={i}>
            <span role="cell" data-id="element-117" data-mp="ql4tuncjveov">
              <span className={`f ${FORCE_CLASS[r.force]}`} data-id="element-118" data-mp="ltdc92bdgjav">{r.force}</span>
            </span>
            <span role="cell" data-id="element-119" data-mp="t49lyocbqemn">
              {r.rule}
              {r.note && <span className="cf" data-id="element-120" data-mp="t62te5urh2vp">{r.note}</span>}
            </span>
            <span role="cell" className="tg" data-id="element-121" data-mp="y4e38gl23ddx">
              {r.trigger}
            </span>
            <span role="cell" className="sc" data-id="element-122" data-mp="13264403oi9u9">
              {r.source}
            </span>
            <span role="cell" className="qw" title={r.words} data-id="element-123" data-mp="1821z59ueeqxo">
              {r.words}
            </span>
          </div>)}
        <div className="lcount" data-id="element-124" data-mp="1c2cg1hjqb1rb">
          {shown} of {total} shown
        </div>
      </div>

      <div data-id="element-125" data-mp="1h0w35la26jl9">
        <div className="ledger-ft" data-id="element-126" data-mp="1h0mcdvh6w8ei">
          <div className="s" data-id="element-127" data-mp="1lsxacssddk4f">Showing {shown} of {total}. The rest have owners, versions, and triggers waiting.</div>
          {!libOpen && <button type="button" className="btn btn-s" style={{
          height: 44
        }} onClick={() => setLibOpen(true)} data-id="element-128" data-mp="1qqytfdpspjfw">
              Add to your library
            </button>}
        </div>
        {libOpen && <div className="libform" data-id="element-129" data-mp="1zt817vtoiypl">
            <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }} data-id="element-130" data-mp="2xhl4253jdmz1">
              <div className="t" data-id="element-131" data-mp="2xfgnonogavhv">Your library starts here.</div>
              <div className="b" data-id="element-132" data-mp="2shf481n44u5v">
                These {total} rules, versioned and owned by you, become the first entries. Tell us where to send the keys.
              </div>
              <div className="s" data-id="element-133" data-mp="327rri1mbb1fp">
                What you see is the plain-English face of each rule. Underneath, it’s hashed, versioned, traced to its source, and finalized through twelve processes.
              </div>
            </div>
            {send === 'sent' ? <div className="ok" role="status" data-id="element-134" data-mp="2njdo9pkzhaym">
                Got it. We’ll be in touch at {fields.email} with your keys.
              </div> : <form onSubmit={e => {
          e.preventDefault();
          submitLibrary(fields);
        }} data-id="element-135" data-mp="2iir2yejcktov">
                {([['name', 'Name', 'text'], ['email', 'Work email', 'email'], ['company', 'Company', 'text']] as const).map(([key, label, type]) => <React.Fragment key={key} data-id="element-136" data-mp="3siaae44l9owc">
                    <label htmlFor={`lib-${key}`} className="sr" data-id="element-137" data-mp="3sjloawrr0d16">
                      {label}
                    </label>
                    <input id={`lib-${key}`} className="inp" type={type} required placeholder={label} value={fields[key]} onChange={e => setFields(f => ({
              ...f,
              [key]: e.target.value
            }))} data-id="element-138" data-mp="1c956c7b1pim" />
                  </React.Fragment>)}
                <button className="btn btn-s" type="submit" disabled={send === 'sending'} style={{
            justifyContent: 'center',
            marginTop: 4
          }} data-id="element-139" data-mp="k8ihatync5zz">
                  {send === 'sending' ? 'Sending…' : 'Start my library'}
                </button>
                {send === 'error' && <div className="err" role="alert" data-id="element-140" data-mp="1mmd2hrlis5ol">
                    That didn’t go through. Try again, or email hello@meetsynthia.ai.
                  </div>}
              </form>}
          </div>}
      </div>
    </div>;
});